import { useEffect, useMemo, useRef, useState } from 'react';
import { skills } from '../content/skills.js';
import { useProgress, recordSpeedRound } from '../store/progress.js';
import { correctTap, wrongTap, tapLight } from '../utils/haptics.js';
import { BALL } from '../theme.js';
import { Icons } from './Icons.jsx';
import { ChunkyButton, GhostButton } from './Buttons.jsx';
import { Maja } from './Mascot.jsx';
import { t } from '../i18n.js';
import { Sentry } from '../sentry.js';

const T = BALL;
const DURATION_S = 60;
const WRONG_PENALTY_S = 2;

function shuffle(arr) {
  const a = arr.slice();
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

function pickDistractors(pool, correct, count, key) {
  const candidates = pool
    .filter((p) => p[key] && p[key] !== correct[key])
    .map((p) => p[key]);
  const unique = Array.from(new Set(candidates));
  return shuffle(unique).slice(0, count);
}

function buildPool(progress) {
  // Items from skills the user has at least started (any completed lessons).
  const items = [];
  for (const s of skills) {
    const completed = progress.skillProgress[s.id]?.completed || 0;
    if (completed > 0) items.push(...s.items);
  }
  return items;
}

function makeQuestion(pool, sourceLang) {
  if (pool.length < 4) return null;
  const item = pool[Math.floor(Math.random() * pool.length)];
  const direction = Math.random() < 0.5 ? 'toHR' : 'fromHR';
  const key = direction === 'toHR' ? 'hr' : sourceLang;
  const promptKey = direction === 'toHR' ? sourceLang : 'hr';
  const distractors = pickDistractors(pool, item, 3, key);
  if (distractors.length < 3) return null;
  const options = shuffle([item[key], ...distractors]);
  return {
    direction,
    prompt: item[promptKey],
    correct: item[key],
    options,
  };
}

export default function SpeedRound({ onExit }) {
  const progress = useProgress();
  const lang = progress.sourceLang;
  const pool = useMemo(() => buildPool(progress), [progress.skillProgress]);

  const [timeLeftMs, setTimeLeftMs] = useState(DURATION_S * 1000);
  const [score, setScore] = useState(0);
  const [question, setQuestion] = useState(() => makeQuestion(pool, lang));
  const [picked, setPicked] = useState(null);
  const [finished, setFinished] = useState(false);
  const finishedRef = useRef(false);

  // Tick the timer every 100ms — finer than per-second so the penalty
  // visibly subtracts when you get a wrong answer.
  useEffect(() => {
    if (finished) return;
    const start = Date.now();
    let last = start;
    const id = setInterval(() => {
      const now = Date.now();
      const dt = now - last;
      last = now;
      setTimeLeftMs((t) => {
        const next = t - dt;
        if (next <= 0) {
          clearInterval(id);
          finish();
          return 0;
        }
        return next;
      });
    }, 100);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [finished]);

  function finish() {
    if (finishedRef.current) return;
    finishedRef.current = true;
    setFinished(true);
    recordSpeedRound(score);
    Sentry.addBreadcrumb({ category: 'speed-round', message: 'finish', data: { score } });
  }

  function pick(opt) {
    if (finished || picked) return;
    const correct = opt === question.correct;
    setPicked(opt);
    if (correct) {
      setScore((s) => s + 1);
      correctTap();
    } else {
      setTimeLeftMs((t) => Math.max(0, t - WRONG_PENALTY_S * 1000));
      wrongTap();
    }
    // Immediately advance — no feedback pause.
    setTimeout(() => {
      setPicked(null);
      setQuestion(makeQuestion(pool, lang));
    }, 180);
  }

  function restart() {
    tapLight();
    finishedRef.current = false;
    setScore(0);
    setTimeLeftMs(DURATION_S * 1000);
    setQuestion(makeQuestion(pool, lang));
    setPicked(null);
    setFinished(false);
  }

  // ─── Finished screen ───
  if (finished) {
    const xpEarned = Math.min(30, Math.floor(score / 2));
    const newBest = score > (progress.speedRoundBest || 0);
    return (
      <div className="app-shell" style={{ padding: 24 }}>
        <div style={{
          flex: 1, display: 'flex', flexDirection: 'column',
          alignItems: 'center', justifyContent: 'center', gap: 16,
        }}>
          <Maja size={140} mood="cheer" tshirt={newBest ? 'TOPP!' : 'BRA!'} tshirtColor={T.gold} />
          <h1 style={{ fontSize: 28, fontWeight: 900, color: T.ink, marginTop: 12 }}>
            {t('speedRoundTimeUp', lang)}
          </h1>
          <div style={{
            display: 'flex', gap: 10, marginTop: 4, justifyContent: 'center',
          }}>
            <StatTile label={t('score', lang)} value={score} color={T.primary} />
            <StatTile label={t('speedRoundBest', lang)} value={Math.max(score, progress.speedRoundBest || 0)} color={T.gold} />
            <StatTile label="XP" value={`+${xpEarned}`} color={T.blue} />
          </div>
        </div>
        <div style={{
          padding: '20px 0 calc(env(safe-area-inset-bottom) + 8px)',
          display: 'flex', gap: 10, justifyContent: 'center',
        }}>
          <GhostButton size="md" onClick={onExit}>
            {t('back', lang)}
          </GhostButton>
          <ChunkyButton color="primary" onClick={restart}>
            {t('again', lang)}
          </ChunkyButton>
        </div>
      </div>
    );
  }

  // ─── Playing ───
  const seconds = Math.ceil(timeLeftMs / 1000);
  const directionLabel = !question ? '' :
    question.direction === 'toHR'
      ? (lang === 'en' ? 'Translate to Croatian' : 'Översätt till kroatiska')
      : (lang === 'en' ? 'What does this mean?' : 'Vad betyder detta?');

  return (
    <div className="app-shell">
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: 'calc(env(safe-area-inset-top) + 12px) 16px 8px',
      }}>
        <button
          type="button"
          onClick={onExit}
          style={{ appearance: 'none', background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}
        >
          {Icons.close(T.inkSoft)}
        </button>
        <div style={{
          fontSize: 14, fontWeight: 900, color: T.inkSoft, letterSpacing: '0.04em',
        }}>
          {t('score', lang).toUpperCase()} <span style={{ color: T.ink }}>{score}</span>
        </div>
      </div>

      <div style={{
        padding: '6px 16px 14px', display: 'flex', justifyContent: 'center',
      }}>
        <div style={{
          fontSize: 64, fontWeight: 900, color: seconds <= 10 ? T.red : T.primary,
          letterSpacing: '-0.02em', lineHeight: 1,
          textShadow: `0 4px 0 ${seconds <= 10 ? T.redDeep : T.primaryDeep}`,
        }}>
          {seconds}
        </div>
      </div>

      <div className="app-scroll" style={{ padding: '4px 22px 24px' }}>
        {question ? (
          <>
            <div style={{
              fontSize: 11, fontWeight: 800, color: T.blue, letterSpacing: '0.12em',
            }}>
              {directionLabel.toUpperCase()}
            </div>
            <h2 style={{
              fontSize: 28, fontWeight: 900, color: T.ink, margin: '10px 0 18px', lineHeight: 1.15,
            }}>{question.prompt}</h2>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
              {question.options.map((opt) => {
                const isPick = picked === opt;
                const isRight = isPick && opt === question.correct;
                const isWrong = isPick && opt !== question.correct;
                let borderColor = T.hairline;
                let shadowColor = T.hairline;
                let color = T.ink;
                if (isRight) { borderColor = T.mint; shadowColor = T.mintDeep; color = T.mintDeep; }
                else if (isWrong) { borderColor = T.red; shadowColor = T.redDeep; color = T.redDeep; }
                return (
                  <button
                    key={opt}
                    type="button"
                    onClick={() => pick(opt)}
                    disabled={!!picked}
                    style={{
                      appearance: 'none', cursor: picked ? 'not-allowed' : 'pointer',
                      background: T.bgAlt,
                      border: `2px solid ${borderColor}`,
                      boxShadow: `0 4px 0 ${shadowColor}`,
                      borderRadius: 14, padding: '16px 12px',
                      fontWeight: 800, fontSize: 17, color,
                      minHeight: 64, fontFamily: 'inherit',
                      transition: 'border-color 100ms, color 100ms, box-shadow 100ms',
                    }}
                  >
                    {opt}
                  </button>
                );
              })}
            </div>
          </>
        ) : (
          <div style={{ padding: '40px 0', textAlign: 'center', color: T.inkSoft }}>
            {lang === 'en' ? 'Loading…' : 'Laddar…'}
          </div>
        )}
      </div>
    </div>
  );
}

function StatTile({ label, value, color }) {
  return (
    <div style={{
      background: T.bgAlt, border: `2px solid ${T.hairline}`,
      borderRadius: 14, padding: '14px 12px',
      boxShadow: `0 3px 0 ${T.hairline}`,
      display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4,
      minWidth: 96,
    }}>
      <div style={{ fontSize: 22, fontWeight: 900, color }}>{value}</div>
      <div style={{
        fontSize: 11, fontWeight: 700, color: T.inkSoft,
        letterSpacing: '0.04em', textTransform: 'uppercase',
      }}>{label}</div>
    </div>
  );
}
